import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { LEDGER_ARS_PER_USD_EQUIVALENT, ledgerUsdEquivalent } from "@/lib/ledger-money-display";

/** Alineado con api/lib/investorKpiMetrics.ts */
export const INVESTOR_TRACKING_START = "2026-05-03";
export const INVESTOR_TRACKING_LABEL = "3 may 2026";
export const ARG_REPORTING_TZ = "America/Argentina/Buenos_Aires";
export const INVESTOR_ARS_PER_USD = LEDGER_ARS_PER_USD_EQUIVALENT;

export const MEASUREMENT_PERIODS_2026 = [
  { id: 1, from: "2026-05-03", to: "2026-06-02", days: 31 },
  { id: 2, from: "2026-06-03", to: "2026-07-02", days: 30 },
  { id: 3, from: "2026-07-03", to: "2026-08-01", days: 30 },
  { id: 4, from: "2026-08-02", to: "2026-08-31", days: 30 },
  { id: 5, from: "2026-09-01", to: "2026-09-30", days: 30 },
] as const;

export type MeasurementPeriod = (typeof MEASUREMENT_PERIODS_2026)[number];

export type ReportingPeriodRevenueRow = {
  period_id: number;
  from: string;
  to: string;
  days: number;
  revenue_usd: number;
  revenue_ars: number;
  revenue_combined_usd: number;
  transaction_count: number;
  usd_pct_change: number | null;
  ars_pct_change: number | null;
  combined_pct_change: number | null;
};

export type WeeklyRevenueRow = {
  week_start: string;
  week_end: string;
  revenue_usd: number;
  revenue_ars: number;
  revenue_combined_usd: number;
  transaction_count: number;
  usd_pct_change: number | null;
  ars_pct_change: number | null;
  combined_pct_change: number | null;
  is_current_week: boolean;
};

export type DailyVolumeRow = {
  date: string;
  transactions: number;
  unique_users: number;
  revenue_usd: number;
  revenue_ars: number;
  revenue_combined_usd: number;
  cumulative_combined_usd: number;
  arpu: number;
};

export type MrrEvolutionMonthRow = {
  month: string;
  stripe_recurring_usd: number;
  mercadopago_recurring_ars: number;
  /** Recurrente real cobrado (USD equivalente). */
  combined_usd: number;
  pct_change_combined: number | null;
  /** MRR proyectado a cobrar — solo mes actual. */
  run_rate_combined_usd: number | null;
  run_rate_stripe_usd: number | null;
  run_rate_mp_ars: number | null;
  active_subscriptions: number | null;
  /** @deprecated Usar run_rate_combined_usd */
  is_api_snapshot?: boolean;
};

export type MrrEvolutionPeriodRow = {
  period_id: number;
  from: string;
  to: string;
  stripe_recurring_usd: number;
  mercadopago_recurring_ars: number;
  combined_usd: number;
  pct_change_combined: number | null;
  active_at_end: number;
  new_subscriptions: number;
  lost_from_previous_period: number;
  cancelled_in_period: number;
  mrr_run_rate_combined_usd: number | null;
};

export type SubscriptionChurnPeriodRow = {
  period_id: number;
  from: string;
  to: string;
  days: number;
  payers_previous_period?: number;
  payers_in_period?: number;
  active_at_start: number;
  active_at_end: number;
  new_subscriptions: number;
  lost_from_previous_period: number;
  cancelled_in_period: number;
  paused_in_period: number;
  net_subscription_change: number;
  churn_rate_period: number;
  churn_rate_pct_change: number | null;
};

export type InvestorTrackingTotals = {
  total_revenue_usd: number;
  total_revenue_ars: number;
  total_transactions: number;
};

export type InvestorTrackingWindow = {
  start_date: string;
  end_date: string;
  days: number;
  label: string;
};

export type TopSpenderRow = {
  email: string;
  total_usd_equivalent: number;
  transaction_count: number;
};

export type InvestorKpiMetrics = {
  type: "kpi-metrics";
  tracking_window: InvestorTrackingWindow;
  revenue_metrics: InvestorTrackingTotals & {
    paying_users: number;
    arppu: number;
    arpu?: number;
    estimated_ltv: number;
    avg_transaction_usd?: number;
    avg_transaction_ars?: number;
    avg_transactions_per_user: number;
    paying_users_lifetime?: number;
    total_revenue_usd_lifetime?: number;
    total_revenue_ars_lifetime?: number;
    total_revenue_combined_usd?: number;
    total_revenue_combined_usd_lifetime?: number;
    total_transactions_lifetime?: number;
    avg_transactions_per_user_lifetime?: number;
  };
  exchange_rate_ars_per_usd?: number;
  reporting_period_revenue: ReportingPeriodRevenueRow[];
  weekly_revenue: WeeklyRevenueRow[];
  subscription_churn_by_period?: SubscriptionChurnPeriodRow[];
  daily_volume?: DailyVolumeRow[];
  conversion_funnel: {
    total_registered: number;
    users_who_paid: number;
    new_users_30d: number;
    new_users_who_paid: number;
    overall_conversion_rate: number;
    new_user_conversion_rate: number;
  };
  api_data?: {
    reference_only?: boolean;
    note?: string;
    stripe?: { total_charges: number; total_amount_usd: number };
    mercadopago?: { total_amount_ars: number };
    synced_at?: string;
  } | null;
  revenue_data_truth?: Record<string, string>;
  churn_data_truth?: Record<string, string>;
  top_spenders?: TopSpenderRow[];
  mrr?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type PeriodStatus = {
  isCurrent: boolean;
  isClosed: boolean;
  isUpcoming: boolean;
  elapsedDays: number;
  progressPct: number;
};

export type PeriodTotals = {
  usd: number;
  ars: number;
  combinedUsd: number;
  txs: number;
};

export function combineInvestorRevenueUsd(usd: unknown, ars: unknown): number {
  return Number(ledgerUsdEquivalent(safeNumber(usd), safeNumber(ars)).toFixed(2));
}

export function safeNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function safeNullablePct(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
}

export function safeYmd(value: unknown, fallback = ""): string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
  return value;
}

export function getArgentinaYmd(d = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ARG_REPORTING_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function parseYmdToUtcNoon(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
}

export function diffCalendarDays(fromYmd: string, toYmd: string): number {
  const a = parseYmdToUtcNoon(fromYmd).getTime();
  const b = parseYmdToUtcNoon(toYmd).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

export function getCurrentMeasurementPeriod(todayAr = getArgentinaYmd()): MeasurementPeriod {
  for (const period of MEASUREMENT_PERIODS_2026) {
    if (todayAr >= period.from && todayAr <= period.to) return period;
  }
  if (todayAr < MEASUREMENT_PERIODS_2026[0].from) return MEASUREMENT_PERIODS_2026[0];
  return MEASUREMENT_PERIODS_2026[MEASUREMENT_PERIODS_2026.length - 1];
}

export function getPeriodStatusFromRange(
  start: string,
  end: string,
  days: number,
  todayAr = getArgentinaYmd(),
): PeriodStatus {
  const safeDays = Math.max(1, days);
  const isCurrent = todayAr >= start && todayAr <= end;
  const isClosed = todayAr > end;
  const isUpcoming = todayAr < start;
  const elapsedDays = isCurrent
    ? Math.max(1, Math.min(safeDays, diffCalendarDays(start, todayAr) + 1))
    : isClosed
      ? safeDays
      : 0;
  const progressPct = isCurrent
    ? Math.min(100, Math.round((elapsedDays / safeDays) * 100))
    : isClosed
      ? 100
      : 0;
  return { isCurrent, isClosed, isUpcoming, elapsedDays, progressPct };
}

export function formatPeriodRange(from: string, to: string, withYear = false): string {
  try {
    const start = format(parseISO(from), withYear ? "d MMM yyyy" : "d MMM", { locale: es });
    const end = format(parseISO(to), withYear ? "d MMM yyyy" : "d MMM", { locale: es });
    return `${start} → ${end}`;
  } catch {
    return `${from} → ${to}`;
  }
}

export function formatDecimal(value: unknown, digits = 1, fallback = 0): string {
  return safeNumber(value, fallback).toFixed(digits);
}

export function formatInvestorMoney(amount: unknown, currency: "USD" | "ARS"): string {
  const n = safeNumber(amount, 0);
  if (currency === "USD") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  }
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function sumReportingPeriodTotals(periods: ReportingPeriodRevenueRow[]): PeriodTotals {
  return periods.reduce(
    (acc, p) => ({
      usd: acc.usd + safeNumber(p.revenue_usd),
      ars: acc.ars + safeNumber(p.revenue_ars),
      combinedUsd: acc.combinedUsd + safeNumber(p.revenue_combined_usd),
      txs: acc.txs + safeNumber(p.transaction_count),
    }),
    { usd: 0, ars: 0, combinedUsd: 0, txs: 0 },
  );
}

export function periodTotalsDifferFromTracking(
  periodTotals: PeriodTotals,
  tracking: InvestorTrackingTotals,
): boolean {
  return (
    Math.abs(periodTotals.usd - safeNumber(tracking.total_revenue_usd)) > 0.01 ||
    periodTotals.txs !== safeNumber(tracking.total_transactions)
  );
}

function normalizeReportingPeriod(raw: unknown, index: number): ReportingPeriodRevenueRow {
  const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const catalog = MEASUREMENT_PERIODS_2026[index] ?? MEASUREMENT_PERIODS_2026[MEASUREMENT_PERIODS_2026.length - 1];
  const periodId = safeNumber(row.period_id, catalog.id);
  const catalogMatch = MEASUREMENT_PERIODS_2026.find((p) => p.id === periodId) ?? catalog;
  return {
    period_id: periodId,
    from: safeYmd(row.from, catalogMatch.from),
    to: safeYmd(row.to, catalogMatch.to),
    days: safeNumber(row.days, catalogMatch.days),
    revenue_usd: safeNumber(row.revenue_usd),
    revenue_ars: safeNumber(row.revenue_ars),
    revenue_combined_usd: safeNumber(
      row.revenue_combined_usd,
      combineInvestorRevenueUsd(row.revenue_usd, row.revenue_ars),
    ),
    transaction_count: safeNumber(row.transaction_count),
    usd_pct_change: safeNullablePct(row.usd_pct_change),
    ars_pct_change: safeNullablePct(row.ars_pct_change),
    combined_pct_change: safeNullablePct(row.combined_pct_change),
  };
}

function normalizeApiData(raw: unknown): InvestorKpiMetrics["api_data"] {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const stripeRaw =
    a.stripe && typeof a.stripe === "object" ? (a.stripe as Record<string, unknown>) : null;
  const mpRaw =
    a.mercadopago && typeof a.mercadopago === "object"
      ? (a.mercadopago as Record<string, unknown>)
      : null;

  const stripe =
    stripeRaw != null
      ? {
          total_charges: safeNumber(stripeRaw.total_charges),
          total_amount_usd: safeNumber(stripeRaw.total_amount_usd),
        }
      : undefined;
  const mercadopago =
    mpRaw != null
      ? {
          total_amount_ars: safeNumber(mpRaw.total_amount_ars),
        }
      : undefined;

  if (!stripe && !mercadopago && typeof a.synced_at !== "string") return null;

  return {
    ...(a.reference_only === true ? { reference_only: true } : {}),
    ...(typeof a.note === "string" ? { note: a.note } : {}),
    ...(stripe ? { stripe } : {}),
    ...(mercadopago ? { mercadopago } : {}),
    ...(typeof a.synced_at === "string" ? { synced_at: a.synced_at } : {}),
  };
}

function normalizeWeeklyRevenue(raw: unknown): WeeklyRevenueRow {
  const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const weekStart = safeYmd(row.week_start);
  let weekEnd = safeYmd(row.week_end);
  if (weekStart && !weekEnd) {
    try {
      const end = parseYmdToUtcNoon(weekStart);
      end.setUTCDate(end.getUTCDate() + 6);
      weekEnd = end.toISOString().slice(0, 10);
    } catch {
      weekEnd = weekStart;
    }
  }
  return {
    week_start: weekStart,
    week_end: weekEnd,
    revenue_usd: safeNumber(row.revenue_usd),
    revenue_ars: safeNumber(row.revenue_ars),
    revenue_combined_usd: safeNumber(
      row.revenue_combined_usd,
      combineInvestorRevenueUsd(row.revenue_usd, row.revenue_ars),
    ),
    transaction_count: safeNumber(row.transaction_count),
    usd_pct_change: safeNullablePct(row.usd_pct_change),
    ars_pct_change: safeNullablePct(row.ars_pct_change),
    combined_pct_change: safeNullablePct(row.combined_pct_change),
    is_current_week: row.is_current_week === true,
  };
}

function normalizeDailyVolumeRow(raw: unknown): DailyVolumeRow {
  const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const usd = safeNumber(row.revenue_usd);
  const ars = safeNumber(row.revenue_ars);
  return {
    date: safeYmd(row.date),
    transactions: safeNumber(row.transactions),
    unique_users: safeNumber(row.unique_users),
    revenue_usd: usd,
    revenue_ars: ars,
    revenue_combined_usd: safeNumber(row.revenue_combined_usd, combineInvestorRevenueUsd(usd, ars)),
    cumulative_combined_usd: safeNumber(row.cumulative_combined_usd),
    arpu: safeNumber(row.arpu),
  };
}

function normalizeTopSpender(raw: unknown): TopSpenderRow | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const email = typeof row.email === "string" ? row.email.trim() : "";
  if (!email) return null;
  return {
    email,
    total_usd_equivalent: safeNumber(row.total_usd_equivalent),
    transaction_count: safeNumber(row.transaction_count),
  };
}

function normalizeSubscriptionChurnPeriod(raw: unknown, index: number): SubscriptionChurnPeriodRow {
  const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const catalog = MEASUREMENT_PERIODS_2026[index] ?? MEASUREMENT_PERIODS_2026[MEASUREMENT_PERIODS_2026.length - 1];
  const periodId = safeNumber(row.period_id, catalog.id);
  const catalogMatch = MEASUREMENT_PERIODS_2026.find((p) => p.id === periodId) ?? catalog;
  const newSubs = safeNumber(row.new_subscriptions);
  const lost = safeNumber(row.lost_from_previous_period);
  const cancelled = safeNumber(row.cancelled_in_period);
  const payersPrev = safeNumber(row.payers_previous_period, safeNumber(row.active_at_start));
  const payersCur = safeNumber(row.payers_in_period, safeNumber(row.active_at_end));
  return {
    period_id: periodId,
    from: safeYmd(row.from, catalogMatch.from),
    to: safeYmd(row.to, catalogMatch.to),
    days: safeNumber(row.days, catalogMatch.days),
    payers_previous_period: payersPrev,
    payers_in_period: payersCur,
    active_at_start: payersPrev,
    active_at_end: payersCur,
    new_subscriptions: newSubs,
    lost_from_previous_period: lost,
    cancelled_in_period: cancelled,
    paused_in_period: safeNumber(row.paused_in_period),
    net_subscription_change: safeNumber(row.net_subscription_change, newSubs - lost),
    churn_rate_period: safeNumber(row.churn_rate_period),
    churn_rate_pct_change: safeNullablePct(row.churn_rate_pct_change),
  };
}

/** Normaliza payload API: nunca lanza; rellena defaults del catálogo P1–P5. */
export function normalizeInvestorKpiPayload(raw: unknown): InvestorKpiMetrics | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.type !== "kpi-metrics") return null;

  const revenueRaw =
    o.revenue_metrics && typeof o.revenue_metrics === "object"
      ? (o.revenue_metrics as Record<string, unknown>)
      : {};

  const trackingRaw =
    o.tracking_window && typeof o.tracking_window === "object"
      ? (o.tracking_window as Record<string, unknown>)
      : {};

  const funnelRaw =
    o.conversion_funnel && typeof o.conversion_funnel === "object"
      ? (o.conversion_funnel as Record<string, unknown>)
      : {};

  const periodInput = Array.isArray(o.reporting_period_revenue) ? o.reporting_period_revenue : [];
  const reporting_period_revenue = MEASUREMENT_PERIODS_2026.map((catalog, idx) => {
    const fromApi = periodInput.find(
      (p) => p && typeof p === "object" && safeNumber((p as Record<string, unknown>).period_id) === catalog.id,
    );
    return normalizeReportingPeriod(fromApi ?? { period_id: catalog.id, from: catalog.from, to: catalog.to, days: catalog.days }, idx);
  });

  const weeklyInput = Array.isArray(o.weekly_revenue) ? o.weekly_revenue : [];
  const weekly_revenue = weeklyInput
    .map(normalizeWeeklyRevenue)
    .filter((w) => w.week_start.length > 0)
    .sort((a, b) => a.week_start.localeCompare(b.week_start));

  const churnInput = Array.isArray(o.subscription_churn_by_period) ? o.subscription_churn_by_period : [];
  const subscription_churn_by_period = MEASUREMENT_PERIODS_2026.map((catalog, idx) => {
    const fromApi = churnInput.find(
      (p) => p && typeof p === "object" && safeNumber((p as Record<string, unknown>).period_id) === catalog.id,
    );
    return normalizeSubscriptionChurnPeriod(fromApi ?? { period_id: catalog.id, from: catalog.from, to: catalog.to, days: catalog.days }, idx);
  });

  const dailyInput = Array.isArray(o.daily_volume) ? o.daily_volume : [];
  let runningCumulative = 0;
  const daily_volume = dailyInput
    .map(normalizeDailyVolumeRow)
    .filter((d) => d.date.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => {
      const combined = safeNumber(d.revenue_combined_usd);
      runningCumulative = Number((runningCumulative + combined).toFixed(2));
      return {
        ...d,
        cumulative_combined_usd:
          d.cumulative_combined_usd > 0 ? d.cumulative_combined_usd : runningCumulative,
      };
    });

  const todayAr = safeYmd(trackingRaw.end_date, getArgentinaYmd());

  return {
    ...o,
    type: "kpi-metrics",
    tracking_window: {
      start_date: safeYmd(trackingRaw.start_date, INVESTOR_TRACKING_START),
      end_date: todayAr,
      days: Math.max(1, safeNumber(trackingRaw.days, 1)),
      label:
        typeof trackingRaw.label === "string" && trackingRaw.label.trim()
          ? trackingRaw.label
          : `Desde ${INVESTOR_TRACKING_LABEL}`,
    },
    revenue_metrics: {
      total_revenue_usd: safeNumber(revenueRaw.total_revenue_usd),
      total_revenue_ars: safeNumber(revenueRaw.total_revenue_ars),
      total_transactions: safeNumber(revenueRaw.total_transactions),
      paying_users: safeNumber(revenueRaw.paying_users),
      arppu: safeNumber(revenueRaw.arppu),
      arpu: safeNumber(revenueRaw.arpu),
      estimated_ltv: safeNumber(revenueRaw.estimated_ltv),
      avg_transaction_usd: safeNumber(revenueRaw.avg_transaction_usd),
      avg_transaction_ars: safeNumber(revenueRaw.avg_transaction_ars),
      avg_transactions_per_user: safeNumber(revenueRaw.avg_transactions_per_user),
      paying_users_lifetime: safeNumber(revenueRaw.paying_users_lifetime),
      total_revenue_usd_lifetime: safeNumber(revenueRaw.total_revenue_usd_lifetime),
      total_revenue_ars_lifetime: safeNumber(revenueRaw.total_revenue_ars_lifetime),
      total_revenue_combined_usd: safeNumber(
        revenueRaw.total_revenue_combined_usd,
        combineInvestorRevenueUsd(revenueRaw.total_revenue_usd, revenueRaw.total_revenue_ars),
      ),
      total_revenue_combined_usd_lifetime: safeNumber(
        revenueRaw.total_revenue_combined_usd_lifetime,
        combineInvestorRevenueUsd(revenueRaw.total_revenue_usd_lifetime, revenueRaw.total_revenue_ars_lifetime),
      ),
      total_transactions_lifetime: safeNumber(revenueRaw.total_transactions_lifetime),
      avg_transactions_per_user_lifetime: safeNumber(revenueRaw.avg_transactions_per_user_lifetime),
    },
    exchange_rate_ars_per_usd: safeNumber(o.exchange_rate_ars_per_usd, INVESTOR_ARS_PER_USD),
    reporting_period_revenue,
    weekly_revenue,
    subscription_churn_by_period,
    daily_volume,
    conversion_funnel: {
      total_registered: safeNumber(funnelRaw.total_registered),
      users_who_paid: safeNumber(funnelRaw.users_who_paid),
      new_users_30d: safeNumber(funnelRaw.new_users_30d),
      new_users_who_paid: safeNumber(funnelRaw.new_users_who_paid),
      overall_conversion_rate: safeNumber(funnelRaw.overall_conversion_rate),
      new_user_conversion_rate: safeNumber(funnelRaw.new_user_conversion_rate),
    },
    api_data: normalizeApiData(o.api_data),
    top_spenders: (Array.isArray(o.top_spenders) ? o.top_spenders : [])
      .map(normalizeTopSpender)
      .filter((row): row is TopSpenderRow => row !== null),
    mrr: o.mrr && typeof o.mrr === "object" ? (o.mrr as Record<string, unknown>) : null,
  };
}

export function getRecentWeeklyRevenue(weekly: WeeklyRevenueRow[], limit = 8): WeeklyRevenueRow[] {
  if (weekly.length <= limit) return [...weekly];
  return [...weekly].slice(-limit);
}

export type PeriodChartRow = {
  id: number;
  label: string;
  periodLabel: string;
  usd: number;
  ars: number;
  combinedUsd: number;
  isCurrent: boolean;
};

export function buildPeriodChartRows(
  periods: ReportingPeriodRevenueRow[],
  currentPeriodId: number,
): PeriodChartRow[] {
  return periods.map((p) => {
    const label = `P${p.period_id}`;
    let periodLabel = `Período ${p.period_id}`;
    try {
      periodLabel = `P${p.period_id} · ${format(parseISO(p.from), "d MMM", { locale: es })} – ${format(parseISO(p.to), "d MMM yy", { locale: es })}`;
    } catch {
      periodLabel = `P${p.period_id} · ${p.from} – ${p.to}`;
    }
    return {
      id: p.period_id,
      label,
      periodLabel,
      usd: safeNumber(p.revenue_usd),
      ars: safeNumber(p.revenue_ars),
      combinedUsd: safeNumber(p.revenue_combined_usd),
      isCurrent: p.period_id === currentPeriodId,
    };
  });
}

export type CumulativePeriodChartRow = PeriodChartRow & {
  cumulativeCombinedUsd: number;
  pctChange: number | null;
};

export function buildCumulativePeriodChartRows(
  periods: ReportingPeriodRevenueRow[],
  currentPeriodId: number,
): CumulativePeriodChartRow[] {
  let cumulative = 0;
  return buildPeriodChartRows(periods, currentPeriodId).map((row, idx, arr) => {
    cumulative = Number((cumulative + row.combinedUsd).toFixed(2));
    const prev = idx > 0 ? arr[idx - 1].combinedUsd : 0;
    const pctChange = prev > 0 ? Math.round(((row.combinedUsd - prev) / prev) * 100) : null;
    return { ...row, cumulativeCombinedUsd: cumulative, pctChange };
  });
}

export type ChurnChartRow = {
  id: number;
  label: string;
  periodLabel: string;
  activeAtEnd: number;
  newSubs: number;
  cancelled: number;
  churnRate: number;
  isCurrent: boolean;
};

export function buildChurnChartRows(
  churn: SubscriptionChurnPeriodRow[],
  currentPeriodId: number,
): ChurnChartRow[] {
  return churn.map((p) => ({
    id: p.period_id,
    label: `P${p.period_id}`,
    periodLabel: formatPeriodRange(p.from, p.to, true),
    activeAtEnd: safeNumber(p.payers_in_period, p.active_at_end),
    newSubs: safeNumber(p.new_subscriptions),
    cancelled: safeNumber(p.lost_from_previous_period),
    churnRate: safeNumber(p.churn_rate_period),
    isCurrent: p.period_id === currentPeriodId,
  }));
}

export function formatChartDateShort(ymd: string): string {
  try {
    return format(parseISO(ymd), "d/M", { locale: es });
  } catch {
    return ymd;
  }
}

export function formatChartDateLong(ymd: string): string {
  try {
    return format(parseISO(ymd), "d MMM yyyy", { locale: es });
  } catch {
    return ymd;
  }
}

export function computeDailyAverageCombined(daily: DailyVolumeRow[]): number {
  if (daily.length === 0) return 0;
  const total = daily.reduce((s, d) => s + safeNumber(d.revenue_combined_usd), 0);
  return Number((total / daily.length).toFixed(2));
}

export type WeeklyChartRow = {
  key: string;
  label: string;
  weekLabel: string;
  usd: number;
  ars: number;
  combinedUsd: number;
  isCurrent: boolean;
};

export function buildWeeklyChartRows(weeks: WeeklyRevenueRow[]): WeeklyChartRow[] {
  return weeks.map((w) => {
    let label = w.week_start;
    let weekLabel = `${w.week_start} – ${w.week_end}`;
    try {
      label = format(parseISO(w.week_start), "d/M", { locale: es });
      weekLabel = `${format(parseISO(w.week_start), "d MMM", { locale: es })} – ${format(parseISO(w.week_end), "d MMM", { locale: es })}`;
    } catch {
      // keep fallback
    }
    return {
      key: w.week_start,
      label,
      weekLabel,
      usd: safeNumber(w.revenue_usd),
      ars: safeNumber(w.revenue_ars),
      combinedUsd: safeNumber(w.revenue_combined_usd),
      isCurrent: w.is_current_week,
    };
  });
}

export const PCT_CHANGE_CAP = 999;

export function formatPctChangeLabel(pct: number | null, isBaseline = false): string {
  if (isBaseline) return "Base";
  if (pct === null) return "—";
  if (Math.abs(pct) > PCT_CHANGE_CAP) {
    return pct > 0 ? `+${PCT_CHANGE_CAP}%+` : `−${PCT_CHANGE_CAP}%-`;
  }
  return `${pct > 0 ? "+" : ""}${pct}%`;
}

export function isFirstTrackedWeek(weekStart: string, allWeeks: WeeklyRevenueRow[]): boolean {
  const first = allWeeks[0]?.week_start;
  return !!first && weekStart === first;
}